<template>
  <div v-if="currentCloudPost.id" class="w-full">
    <div
      class="w-full flex flex-wrap lg:flex-nowrap transition-all duration-300"
      :class="[post_id === currentCloudPost.id ? 'h-full' : 'h-64']"
    >
      <!-- Left Col -->
      <div
        :class="[
          post_id === currentCloudPost.id
            ? 'translate-x-0 opacity-100'
            : 'opacity-0 -translate-x-8',
        ]"
        class="w-full lg:w-4/12 pr-4 pb-8 lg:pb-0 mb-8 lg:mb-0 border-b lg:border-b-0 border-truegray-300 flex flex-col space-y-4 transform transition-all duration-500"
      >
        <!-- Not editing/public -->
        <div v-if="!editing || previewing">
          <button
            v-if="currentCloudPost.category"
            class="mb-4 rounded bg-truegray-500 py-2 px-4 text-white cursor-default"
          >
            <h6>{{ currentCloudPost.category_title }}</h6>
          </button>

          <p
            v-if="
              (currentCloudPost.year_tag &&
                currentCloudPost.year_tag.length &&
                !editing) ||
              previewing
            "
            class="text-cw-alt font-semibold font-xs uppercase mb-2"
          >
            <span v-for="(yearTag, index) in currentCloudPost.year_tag">
              <span v-if="index"> | </span>
              {{ yearTag }}
            </span>
          </p>
          <h2>{{ currentCloudPost.title }}</h2>

          <!-- tags -->
          <MultipleTags
            v-if="
              currentCloudPost.industry_tags &&
              currentCloudPost.industry_tags.length > 0
            "
            :tags="currentCloudPost.industry_tags"
            :colour="'text-primary'"
            :titles="true"
            :flex="false"
            :cloud="true"
          />
          <PostTags
            v-if="
              currentCloudPost.general_tags &&
              currentCloudPost.general_tags.length > 0
            "
            :tags="currentCloudPost.general_tags"
          />
          <!-- /tags -->

          <!-- Publish date -->
          <div
            v-if="currentCloudPost.created_at && currentCloudPost.category != 3"
            class=""
          >
            <p class="italic mb-8 text-truegray-700 font-semibold text-sm">
              Published: {{ currentCloudPost.created_at_ui }}
            </p>
          </div>
          <!-- /Publish date -->

          <!-- Event Specific -->
          <div v-if="currentCloudPost.category === 3">
            <!-- Dates -->
            <div
              v-if="currentCloudPost.event_start_at_ui"
              class="mb-2 flex items-center text-truegray-700 pt-12"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clip-rule="evenodd"
                />
              </svg>
              <div>
                <span>{{ currentCloudPost.event_start_at_ui }}</span>
                <span v-if="currentCloudPost.event_end_at_ui">
                  - {{ currentCloudPost.event_end_at_ui }}
                </span>
              </div>
            </div>
            <!-- /Dates -->

            <!-- Time -->
            <div
              v-if="currentCloudPost.event_start_at_time"
              class="mb-2 flex items-center text-truegray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ currentCloudPost.event_start_at_time }}
            </div>
            <!-- /Time -->

            <!-- Venue Title -->
            <div
              v-if="currentCloudPost.event_venue_title"
              class="mb-2 flex items-center text-truegray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ currentCloudPost.event_venue_title }}
            </div>
            <!-- /Venue Title -->

            <!-- Venue Address -->
            <div
              v-if="currentCloudPost.event_venue_address"
              class="mb-2 flex text-truegray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ currentCloudPost.event_venue_address }}
            </div>
            <!-- /Venue Address -->
          </div>
          <!-- /Event Specific -->

          <!-- Job Specific -->
          <div v-if="currentCloudPost.category === 2">
            <!-- Job Type -->
            <div
              v-if="currentCloudPost.job_type"
              class="mb-2 flex text-truegray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clip-rule="evenodd"
                />
              </svg>
              <p class="uppercase text-sm font-semibold">
                {{ jobTypes[currentCloudPost.job_type].text }}
              </p>
            </div>
            <!-- /Job Type -->

            <!-- Job Employer -->
            <div
              v-if="currentCloudPost.job_employer"
              class="mb-2 flex text-truegray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                  clip-rule="evenodd"
                />
              </svg>
              <p>{{ currentCloudPost.job_employer }}</p>
            </div>
            <!-- /Job Employer -->

            <!-- Expiration -->
            <p
              v-if="currentCloudPost.event_end_at_ui"
              class="text-red-400 italic text-sm"
            >
              Application Expiry: {{ currentPost.event_end_at_ui }}
            </p>
            <!-- /Expiration -->
          </div>
          <!-- /Job Specific -->

          <!-- Resources -->
          <div
            v-if="
              currentCloudPost.resources &&
              currentCloudPost.resources.length > 0
            "
            class="mt-8 space-y-4"
          >
            <h3>Attached resources</h3>
            <div class="bg-truegray-100 space-y-1">
              <div
                v-for="(resource, i) in currentCloudPost.resources"
                :id="resource.id"
                :key="i"
                :ref="'resource-' + i"
                class="flex text-truegray-500 transition-all duration-100 hover:bg-truegray-300 cursor-pointer w-full py-2 px-6"
                @click="handleResourceClick(resource)"
              >
                <svg
                  v-if="resource.type === 2"
                  xmlns="http://www.w3.org/2000/svg"
                  class="flex-shrink-0 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                    clip-rule="evenodd"
                  />
                </svg>
                <svg
                  v-if="resource.type === 1"
                  xmlns="http://www.w3.org/2000/svg"
                  class="flex-shrink-0 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <svg
                  v-if="resource.type === 3"
                  xmlns="http://www.w3.org/2000/svg"
                  class="flex-shrink-0 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clip-rule="evenodd"
                  />
                </svg>
                <p class="ml-4">{{ resource.title }}</p>
                <a
                  :id="'resource:' + resource.id"
                  target="_blank"
                  class="hover:text-black hidden"
                  :href="resource.file_url"
                  >Download</a
                >
              </div>
            </div>
          </div>
          <!-- /Resources -->

          <a
            v-if="currentCloudPost.link_title"
            class="mt-8 btn btn-cw-700 no-underline w-48 text-center"
            target="_blank"
            :href="currentCloudPost.link_url"
          >
            {{ currentCloudPost.link_title }}
          </a>
        </div>

        <!-- AUTHOR INFO -->
        <div
          v-if="currentCloudPost.author"
          @click="showProfile(currentCloudPost.author)"
        >
          <div class="my-8 flex space-x-2 items-center w-3/4 cursor-pointer">
            <div
              class="rounded-full flex items-center justify-center border-2 border-truegray-100 w-12 h-12"
            >
              <div
                v-if="currentCloudPost.author.logo"
                class="bg-no-repeat bg-cover bg-center w-8 h-8 rounded-full"
                :style="{
                  backgroundImage: 'url(' + currentCloudPost.author.logo + ')',
                }"
              ></div>
              <div
                v-else
                class="flex justify-center items-center w-8 h-8 rounded-full text-cw-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div class="flex flex-col justify-center">
              <h6
                v-if="currentCloudPost.author.title"
                :style="{ maxWidth: '180px' }"
                class="text-sm text-truegray-500 truncate overflow-ellipsis"
              >
                {{ currentCloudPost.author.title }}
              </h6>
              <h6 v-if="currentCloudPost.author.post_count" class="text-cw-alt">
                Posts: {{ currentCloudPost.author.post_count }}
              </h6>
            </div>
          </div>
        </div>
        <!-- Connections -->
        <div
          v-if="!!currentCloudPost.contacts?.length"
          class="flex flex-col pt-4"
        >
          <h3>Attached connections</h3>
          <div class="flex flex-col gap-4">
            <ListConnection
              v-for="connection in currentCloudPost.contacts"
              :key="connection.id"
              :connection="connection"
            />
          </div>
        </div>
        <!-- /Not editing/public -->
      </div>
      <!-- /Left Col -->

      <!-- Right Col -->
      <div
        :class="[
          post_id === currentCloudPost.id
            ? 'translate-x-0 opacity-100'
            : 'opacity-0 translate-x-8',
        ]"
        class="w-full lg:w-8/12 lg:border-l border-truegray-300 lg:pl-6 transition-all duration-300 ease-in-out transform transition-all duration-500"
      >
        <!-- /Not editing/public -->
        <div v-if="currentCloudPost.id" class="flex items-center justify-end">
          <button
            v-if="userAccess"
            type="submit"
            class="btn relative bg-red-600 border border-red-600 text-white hover:bg-white hover:text-red-600 active:bg-red-600 active:text-white focus:bg-red-600 focus:text-white"
            @click="deletePost"
          >
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie v-if="thinking" :options="loadingAnimationOptions" />
            </div>
            <span :class="[thinking ? 'invisible' : '']">Delete</span>
          </button>

          <button
            v-if="userAccess"
            type="button"
            class="btn btn-black ml-8"
            @click="editPost"
          >
            Edit post
          </button>
          <button
            v-if="previewing"
            class="btn btn-cw-alt ml-8"
            @click="savePost"
          >
            Save
          </button>
        </div>
        <div v-if="!editing || previewing">
          <img class="w-full" :src="currentCloudPost.image_featured" />
          <h3 class="my-6">{{ currentCloudPost.description }}</h3>
          <!-- <p>{{currentCloudPost.body}}</p> @geoff note that this wont work as vue does not allow html injection like this. See below for correct application -->
          <span class="prose cloud" v-html="currentCloudPost.body"></span>
        </div>
        <!-- /Not editing/public -->
      </div>
      <!-- /Right Col -->

      <!-- Media Modal -->
      <div
        v-if="showMediaModal"
        id="showMediaModal"
        class="fixed z-50 inset-0 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>
          <!-- This element is to trick the browser into centering the modal contents. -->
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >
          <!-- Modal panel, show/hide based on modal state. -->
          <div
            :style="[
              modalLoaded ? { height: modalHeight + 'px' } : {},
              activeResource.type === 2
                ? { height: 'calc(100vh - 10rem)' }
                : {},
            ]"
            :class="modalLoaded ? '' : 'p-8 w-52 h-52'"
            class="bg-cover inline-block align-bottom bg-white border-8 border-white rounded-4xl p-6 text-left shadow transform transition-all duration-300 ease-in-out sm:my-8 sm:align-middle sm:max-w-4xl sm:max-h-lg sm:w-full sm:p-16 relative"
          >
            <div
              :style="[
                activeResource.type === 2
                  ? { backgroundImage: 'url(' + activeResource.file_url + ')' }
                  : {},
              ]"
              class="flex flex-col bg-black absolute bg-center inset-0 w-full h-full rounded-4xl overflow-hidden z-10 bg-no-repeat"
              :class="[activeResource.type !== 1 ? 'bg-contain' : 'bg:cover']"
            >
              <div
                v-if="
                  activeResource.type === 3 &&
                  activeResource.video_type === 'YouTube' &&
                  showMediaModal
                "
                id="YouTube"
                class="z-30 aspect-w-16 aspect-h-9 rounded-4xl"
              >
                <div
                  class="absolute top-0 left-0 h-52 p-16 flex items-center justify-center"
                >
                  <lottie :options="loadingAnimationOptions" />
                </div>
                <iframe
                  ref="resourceVideoPreview"
                  type="iframe"
                  class="rounded z-30"
                  :src="youtube + activeResource.video_code"
                  frameborder="0"
                  controls="0"
                  modestbranding="1"
                  showinfo="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  @load="modalResize"
                ></iframe>
              </div>
              <div
                v-if="
                  activeResource.type === 3 &&
                  activeResource.video_type === 'Vimeo' &&
                  showMediaModal
                "
                id="Vimeo"
                class="z-30 aspect-w-16 aspect-h-9 rounded-4xl"
              >
                <div
                  class="absolute top-0 left-0 h-52 p-16 flex items-center justify-center"
                >
                  <lottie :options="loadingAnimationOptions" />
                </div>
                <iframe
                  ref="resourceVideoPreview"
                  type="iframe"
                  class=""
                  :src="vimeo + activeResource.video_code"
                  controls="0"
                  pip="0"
                  title="0"
                  frameborder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                  @load="modalResize"
                ></iframe>
              </div>
            </div>
            <div class="flex flex-col space-y-4">
              <button
                type="button"
                class="absolute z-40 right-16 top-12 transform hover:rotate-180 transition-all duration-300 text-white hover:text-expo_red"
                @click="closeModal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import PostTags from '../../../shared/components/PostTags.vue'
import MultipleTags from '../../../shared/components/MultipleTags.vue'
import { mapGetters } from 'vuex'
import * as loadingAnimation from '../../../shared/components/loading.json'
import { JOB_TYPES } from '../../../js/util/constants.js'
import ListConnection from '../../shared/connections/components/ListConnection.vue'

export default {
  components: {
    PostTags,
    MultipleTags,
    ListConnection,
  },

  props: {
    post_id: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      thinking: false,

      editing: false,
      stateChanging: false,
      previewing: false,

      loaded: false,

      activeResource: {},
      showMediaModal: false,
      modalLoaded: false,

      modalHeight: null,

      youtube: 'https://www.youtube.com/embed/',
      vimeo: 'https://player.vimeo.com/video/',

      tags: {},
      jobTypes: JOB_TYPES,
    }
  },

  computed: {
    ...mapGetters({
      userAccess: 'shared/getUserAccess',
      currentPost: 'shared/getCurrentPost',
      currentCloudPost: 'careerwise/getCurrentCloudPost',
    }),
  },

  watch: {
    showMediaModal: {
      handler: function (val) {
        EventBus.$emit('modal_state_changing', val)
      },

      deep: true,
    },
  },

  mounted() {
    this.$set(this, 'tags', this.currentCloudPost.tags)
  },

  methods: {
    handleResourceClick(resource) {
      let resourceref = 'resource:' + resource.id
      if (resource.type === 1) {
        // File - ToDo: create file download option
        // this.$refs.resourceref.click();
        document.getElementById(resourceref).click()
      } else {
        // Image
        // this.$refs.resourceref.click();
        this.$set(this, 'activeResource', resource)
        this.$set(this, 'showMediaModal', true)
      }
    },

    closeModal() {
      this.$set(this, 'showMediaModal', false)
      this.$set(this, 'modalLoaded', false)
      this.$set(this, 'activeResource', null)
    },

    modalResize() {
      if (this.activeResource.type === 3) {
        let iframe = false

        if (this.activeResource.video_type === 'Vimeo') {
          iframe = document.getElementById('Vimeo')
        } else {
          iframe = document.getElementById('YouTube')
        }

        this.$set(this, 'modalLoaded', true)
        this.$set(this, 'modalHeight', iframe.scrollHeight)
      } else {
        this.$set(this, 'modalHeight', 'calc(100vh - 10rem)')
        this.$set(this, 'modalLoaded', true)
      }
    },

    showProfile(author) {
      if (author.directory_url) {
        // Favour a local directory listing if exists
        this.$router.push({ path: author.directory_url })
        return
      }

      if (author.site_url) {
        // Go to the tenancy listing if no directory listing and we have a site_url
        window.location.href = author.site_url
        return
      }

      // Neither of the above exists --- do nothing.
      return
    },
  },
}
</script>

<style scoped>
.prose.cloud a {
  color: var(--cw-alt) !important;
}

.tag-item::after {
  position: absolute;
  right: -0.5rem;
  top: 50%;
  content: '';
  background: currentColor;
  height: 4px;
  width: 2px;
  border-radius: 6px;
  transform: translateY(-50%);
}
</style>
